// Career.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCareers } from "../../api/client";
import "./Career.css";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* Perks shown under "Why work with us" — static content, not backend data */
const PERKS = [
  {
    title: "Flexible working hours",
    desc: "Structure your day around focus, not fixed clock-ins.",
    color: "#d81f26",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Learning & upskilling",
    desc: "Access to courses, certifications and internal training tracks.",
    color: "#f2994a",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 6.5 12 3l8 3.5-8 3.5-8-3.5Z" strokeLinejoin="round" />
        <path d="M7 10.5V16c0 1.5 2.2 3 5 3s5-1.5 5-3v-5.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Health coverage",
    desc: "Medical insurance for you, with add-on options for family.",
    color: "#eb5757",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 20.5S4 15.8 4 9.9C4 6.9 6.3 5 8.8 5c1.4 0 2.8.7 3.2 1.9C12.4 5.7 13.8 5 15.2 5c2.5 0 4.8 1.9 4.8 4.9 0 5.9-8 10.6-8 10.6Z" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Remote-friendly",
    desc: "Hybrid setups and remote weeks, based on the role.",
    color: "#2f80ed",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="5" width="18" height="12" rx="1.5" />
        <path d="M8 21h8M12 17v4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Clear growth path",
    desc: "Defined levels and regular reviews, not vague promises.",
    color: "#27ae60",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 20V13M11 20V8M18 20v-6" strokeLinecap="round" />
        <path d="M4 10 11 4l7 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Team culture",
    desc: "Small teams, direct ownership, regular offsites.",
    color: "#9b51e0",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="8" cy="9" r="3" />
        <circle cx="17" cy="9" r="3" />
        <path d="M2.5 20c.6-3 2.8-5 5.5-5s4.9 2 5.5 5M12.5 20c.5-2.6 2.4-4.6 4.7-5 2.2.4 4 2.2 4.5 5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function Career() {
  const navigate = useNavigate();
  const [heroRef, heroIn] = useInView(0.1);
  const [perksRef, perksIn] = useInView(0.1);
  const [jobsRef, jobsIn] = useInView(0.08);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* ────────────────────────────────────────────────────────────
     BACKEND FETCH — loads open positions from the PHP backend
     (careers.php). Response shape: an array of objects like
       { id, title, department, location, type, experience, applyLink }
     No hardcoded roles here — only whatever the backend returns
     is shown. If the list is empty or the call fails, the page
     says so instead of showing fake openings.
     ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;

    fetchCareers()
      .then((data) => {
        if (!cancelled) setJobs(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setJobs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="cr-page">

      {/* ── 1. HERO ── */}
      <section className={`cr-hero${heroIn ? " cr-in" : ""}`} ref={heroRef}>
        <div className="cr-hero__blob cr-hero__blob--1" aria-hidden="true" />
        <div className="cr-hero__blob cr-hero__blob--2" aria-hidden="true" />
        <div className="cr-hero__grid-overlay" aria-hidden="true" />

        <div className="cr-hero__content">
          <h1 className="cr-hero__heading cr-anim" style={{ animationDelay: "0.05s" }}>
            Build what's next<br />
            with <span className="cr-accent">SkillSync Pro.</span>
          </h1>
          <button
            className="cr-btn-primary cr-hero__cta cr-anim"
            style={{ animationDelay: "0.15s" }}
            onClick={() => document.getElementById("open-positions")?.scrollIntoView({ behavior: "smooth" })}
          >
            Current Openings <span className="cr-btn-arrow">→</span>
          </button>
        </div>
      </section>

      {/* ── 2. WHY WORK WITH US — zig-zag timeline layout ── */}
      <section className={`cr-perks-section${perksIn ? " cr-in" : ""}`} ref={perksRef}>
        <div className="cr-section__head">
          <span className="cr-eyebrow">Why Join Us</span>
          <h2 className="cr-heading">What you get, working here</h2>
        </div>

        <div className="cr-perks-timeline">
          <div className="cr-perks-timeline__line" aria-hidden="true" />
          {PERKS.map((perk, i) => (
            <div
              key={perk.title}
              className={`cr-perk-row ${i % 2 === 0 ? "cr-perk-row--left" : "cr-perk-row--right"}`}
              style={{ transitionDelay: `${i * 0.09}s`, "--perk-accent": perk.color }}
            >
              <div className="cr-perk-row__card">
                <span className="cr-perk-row__index">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="cr-perk-row__title">{perk.title}</h3>
                <p className="cr-perk-row__desc">{perk.desc}</p>
              </div>
              <div className="cr-perk-row__node">
                <span className="cr-perk-row__icon">{perk.icon}</span>
              </div>
              <div className="cr-perk-row__spacer" aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>

      {/* ── 3. OPEN POSITIONS — renders whatever the backend returns ── */}
      <section className="cr-positions-section" id="open-positions" ref={jobsRef}>
        <div className="cr-section__head">
          <span className="cr-eyebrow">Open Positions</span>
          <h2 className="cr-heading">Current openings</h2>
          <button
            className="cr-btn-primary cr-positions__cta"
            onClick={() => document.querySelector(".cr-jobs-list")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          >
            View Open Roles <span className="cr-btn-arrow">→</span>
          </button>
        </div>

        {loading && <p className="cr-state-msg">Loading open positions...</p>}

        {!loading && error && (
          <p className="cr-state-msg">Couldn't load open positions right now. Please try again later.</p>
        )}

        {!loading && !error && jobs.length === 0 && (
          <p className="cr-state-msg">No open positions at the moment. Check back soon.</p>
        )}

        <div className={`cr-jobs-list${jobsIn ? " cr-in" : ""}`}>
          {jobs.map((job, i) => (
            <div key={job.id ?? job.title} className="cr-job-card" style={{ transitionDelay: `${i * 0.06}s` }}>
              <div className="cr-job-card__main">
                <h3 className="cr-job-card__title">{job.title}</h3>
                <div className="cr-job-card__meta">
                  {job.department && <span className="cr-job-tag">{job.department}</span>}
                  {job.location && <span className="cr-job-tag">{job.location}</span>}
                  {job.type && <span className="cr-job-tag">{job.type}</span>}
                  {job.experience && <span className="cr-job-tag">{job.experience}</span>}
                </div>
              </div>
              <a
                className="cr-btn-secondary"
                href={job.applyLink || "#"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply <span className="cr-btn-arrow">→</span>
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* ── 4. CTA — general application ── */}
      <section className="cr-cta">
        <div className="cr-cta__inner">
          <h2 className="cr-cta__heading">Don't see the right role?</h2>
          <p className="cr-cta__sub">
            Send your resume anyway. We keep good profiles on file for
            when a matching opening comes up.
          </p>
          <button className="cr-btn-primary" onClick={() => navigate("/contact")}>
            Send Resume <span className="cr-btn-arrow">→</span>
          </button>
        </div>
      </section>

    </div>
  );
}